import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Switch from "components/common/Switch";
import CustomField from "components/form/CustomField";
import { useSmartBack } from "hooks/useSmartBack";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSystemSettings,
  updateSystemSettings,
} from "store/slices/pageContentSlice";
import { handleSubmitForm } from "helpers/apiUtils";
import {
  MAXIMUM_VALUES_MAX_TRANSFER_AND_MAX_PAUSES,
  MEMBERSHIP_PAUSE_BILLING_CYCLE,
  REWARD_TRIGGERING_TYPES_RECEIVER,
  REWARD_TRIGGERING_TYPES_SENDER,
} from "utils/constants";
import SingleSelect from "components/common/SingleSelect";
import {
  loyaltyPointsRedemptionValidationSchema,
  pauseRuleValidationSchema,
  referralRewardValidationSchema,
  transferRuleValidationSchema,
  vatInfoValidationSchema,
} from "utils/validations";
import { Icons } from "assets/icons";
import useConfirmSubmit from "hooks/useConfirmSubmit";
import ConfirmDialog from "components/common/ConfirmDialog";

const AppSettings = () => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { info } = Icons || {};

  const { systemSettings: fetchedSettings } = useSelector(
    (state) => state.PageContent,
  );

  const systemSettings =
    location.state?.systemSettingsData || fetchedSettings?.data;

  useEffect(() => {
    if (!location.state?.systemSettingsData) {
      dispatch(getSystemSettings());
    }
  }, []);

  // ─── Helper: find a setting block by key ───────────────────────────────────
  const getSetting = (key) => systemSettings?.find((s) => s.key === key);

  // ─── Derived data from API response ────────────────────────────────────────
  const forceUpdateSetting = getSetting("forceAppUpdate");
  const maintenanceSetting = getSetting("maintenanceMode");
  const pauseRuleSetting = getSetting("membershipPauseRule");
  const transferRuleSetting = getSetting("membershipTransferRule");
  const vatInfoSetting = getSetting("vatInfo");
  const referralRewardSetting = getSetting("referralReward");
  const loyaltyPointsRedemptionSetting = getSetting("loyaltyPointsRedemption");

  // ─── Initial values (fall back to safe defaults if API not loaded yet) ─────
  const forceUpdateInitial = {
    status: forceUpdateSetting?.status ?? false,
    iOSApp: forceUpdateSetting?.value?.iOSApp ?? false,
    androidApp: forceUpdateSetting?.value?.androidApp ?? false,
    // iOSAppVersion:      forceUpdateSetting?.value?.iOSAppVersion  ?? "",
    // androidAppVersion:  forceUpdateSetting?.value?.androidAppVersion ?? "",
  };

  const maintenanceInitial = {
    iOSApp: maintenanceSetting?.value?.iOSApp ?? false,
    androidApp: maintenanceSetting?.value?.androidApp ?? false,
    website: maintenanceSetting?.value?.website ?? false,
  };
  const pauseRuleInitial = {
    planWiseRules:
      pauseRuleSetting?.value?.planWiseRules?.map((rule) => ({
        enabled: rule.enabled ?? true,
        maxPauseCount: rule.maxPauseCount ?? 1,
        billingCycle: rule.billingCycle,
        minPauseDays: rule.isUnlimited ? 0 : rule.minPauseDays,
        maxPauseDays: rule.isUnlimited ? 0 : rule.maxPauseDays,
        isUnlimited: rule.isUnlimited ?? false,
      })) ?? [],
    autoResume: pauseRuleSetting?.value?.autoResume ?? false,
  };

  const transferRuleInitial = {
    maxTransfers: transferRuleSetting?.value?.maxTransfers ?? "",
    cooldownDays: transferRuleSetting?.value?.cooldownDays ?? "",
    minDaysBeforeTransfer:
      transferRuleSetting?.value?.minDaysBeforeTransfer ?? "",
  };
  const vatInfoInitial = {
    title: vatInfoSetting?.value?.title ?? "",
    titleAr: vatInfoSetting?.value?.titleAr ?? "",
    value: vatInfoSetting?.value?.value ?? "",
    registeredNo: vatInfoSetting?.value?.registeredNo ?? "",
  };
  const referralRewardInitial = {
    senderPoints: referralRewardSetting?.value?.senderPoints ?? "",
    receiverPoints: referralRewardSetting?.value?.receiverPoints ?? "",
    expiryDays: referralRewardSetting?.value?.expiryDays ?? "",
    receiverTriggerOn: referralRewardSetting?.value?.receiverTriggerOn ?? "",
    senderTriggerOn: referralRewardSetting?.value?.senderTriggerOn ?? "",
  };
  const loyaltyPointsRedemptionInitial = {
    pointsPerSAR: loyaltyPointsRedemptionSetting?.value?.pointsPerSAR ?? "",
    minimumRedemptionPoints:
      loyaltyPointsRedemptionSetting?.value?.minimumRedemptionPoints ?? "",
  };

  const createSettingSubmitHandler = (setting, buildPayload) => (values) => {
    if (!setting?._id) return;
    handleSubmitForm({
      dispatch,
      action: updateSystemSettings,
      payload: { id: setting._id, value: buildPayload(values) },
      onSuccess: () => navigate(-1),
    });
  };
  const handleForceUpdateSubmit = createSettingSubmitHandler(
    forceUpdateSetting,
    (v) => ({ iOSApp: v.iOSApp, androidApp: v.androidApp }),
  );

  const handleMaintenanceSubmit = createSettingSubmitHandler(
    maintenanceSetting,
    (v) => ({ iOSApp: v.iOSApp, androidApp: v.androidApp, website: v.website }),
  );

  const handlePauseRuleSubmit = createSettingSubmitHandler(
    pauseRuleSetting,
    (v) => ({
      planWiseRules: v.planWiseRules.map((rule) => ({
        enabled: rule.enabled,
        maxPauseCount: rule.maxPauseCount,
        billingCycle: rule.billingCycle,
        minPauseDays: rule.minPauseDays,
        maxPauseDays: rule.maxPauseDays,
        isUnlimited: rule.isUnlimited,
      })),
      autoResume: v.autoResume,
    }),
  );

  const handleTransferRuleSubmit = createSettingSubmitHandler(
    transferRuleSetting,
    (v) => ({
      maxTransfers: Number(v.maxTransfers),
      cooldownDays: Number(v.cooldownDays),
      minDaysBeforeTransfer: Number(v.minDaysBeforeTransfer),
    }),
  );

  const handleVatInfoSubmit = createSettingSubmitHandler(
    vatInfoSetting,
    (v) => ({
      title: v.title,
      titleAr: v.titleAr,
      value: v.value,
      registeredNo: v.registeredNo,
    }),
  );
  const handleReferralRewardSubmit = createSettingSubmitHandler(
    referralRewardSetting,
    (v) => ({
      senderPoints: v.senderPoints,
      expiryDays: v.expiryDays,
      receiverPoints: v.receiverPoints,
      senderTriggerOn: v.senderTriggerOn,
      receiverTriggerOn: v.receiverTriggerOn,
    }),
  );
  const handleLoyaltyPointsRedemptionsSubmit = createSettingSubmitHandler(
    loyaltyPointsRedemptionSetting,
    (v) => ({
      pointsPerSAR: v.pointsPerSAR,
      minimumRedemptionPoints: v.minimumRedemptionPoints,
    }),
  );

  const forceUpdate = useConfirmSubmit(handleForceUpdateSubmit);
  const maintenance = useConfirmSubmit(handleMaintenanceSubmit);
  const pauseRule = useConfirmSubmit(handlePauseRuleSubmit);
  const transferRule = useConfirmSubmit(handleTransferRuleSubmit);
  const vatInfo = useConfirmSubmit(handleVatInfoSubmit);
  const referral = useConfirmSubmit(handleReferralRewardSubmit);
  const loyalty = useConfirmSubmit(handleLoyaltyPointsRedemptionsSubmit);
  return (
    <div className="page-content">
      <h2 className="page_title">{t("App Settings")}</h2>
      <h3 className="page_sub_title">
        {t("Configure app behavior, force updates, and maintenance mode")}
      </h3>

      {/* ──────────────────── FORCE APP UPDATE ──────────────────── */}
      <div className="common_card mb-4">
        <h4 className="section_title">{t("Force App Update")}</h4>

        <Formik
          initialValues={forceUpdateInitial}
          enableReinitialize // ← re-init when API data arrives
          onSubmit={forceUpdate.handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* iOS Toggle */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Force Update for iOS")}</label>
                    <Switch
                      checked={values.iOSApp}
                      handleOnChange={(e) => setFieldValue("iOSApp", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, iOS users will be forced to update the app to the version you specify below.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Android Toggle */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Force Update for Android")}</label>
                    <Switch
                      checked={values.androidApp}
                      handleOnChange={(e) =>
                        setFieldValue("androidApp", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, Android users will be forced to update the app to the version you specify below.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={forceUpdate.showConfirm}
          onCancel={forceUpdate.handleCancel}
          onConfirm={forceUpdate.handleConfirm}
          title="Save Force Update Settings"
          message="Are you sure you want to update the force app update settings?"
        />
      </div>
      {/* ──────────────────── MAINTENANCE MODE ──────────────────── */}
      <div className="common_card">
        <h4 className="section_title">{t("Maintenance Mode")}</h4>

        <Formik
          initialValues={maintenanceInitial}
          enableReinitialize // ← re-init when API data arrives
          onSubmit={maintenance.handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* iOS Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for iOS")}</label>
                    <Switch
                      checked={values.iOSApp}
                      handleOnChange={(e) => setFieldValue("iOSApp", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, iOS users will see a maintenance mode message and cannot use the app.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Android Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for Android")}</label>
                    <Switch
                      checked={values.androidApp}
                      handleOnChange={(e) =>
                        setFieldValue("androidApp", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, Android users will see a maintenance mode message and cannot use the app.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Website Maintenance */}
                <Col lg={4}>
                  <div className="form_field">
                    <label>{t("Enable Maintenance for Website")}</label>
                    <Switch
                      checked={values.website}
                      handleOnChange={(e) => setFieldValue("website", e.value)}
                    />
                    <p className="text-muted small mt-1">
                      {t(
                        "If enabled, website users will see a maintenance mode message and cannot access the site.",
                      )}
                    </p>
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={maintenance.showConfirm}
          onCancel={maintenance.handleCancel}
          onConfirm={maintenance.handleConfirm}
          title="Save Maintenance Settings"
          message="Are you sure you want to update the maintenance mode settings?"
        />
      </div>
      {/* ──────────────────── MEMBERSHIP PAUSE RULE ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Membership Pause Rule")}</h4>
        <Formik
          initialValues={pauseRuleInitial}
          enableReinitialize
          validationSchema={pauseRuleValidationSchema}
          onSubmit={pauseRule.handleFormSubmit} // 👈
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Row className="g-4">
                {/* Plan Wise Rules */}
                {values.planWiseRules.map((rule, index) => (
                  <React.Fragment key={index}>
                    {index !== 0 && <hr />}
                    <div className="mt-3">
                      <h6
                        className={`mb-1 ${index !== 0 ? "mt-2" : "mt-5"}  text-capitalize`}
                      >
                        {rule.billingCycle.replace("_", " ")}
                      </h6>
                    </div>

                    {/* Enable / Disable */}
                    <Col lg={3}>
                      <div className="form_field">
                        <Switch
                          label={t("Enable")}
                          checked={rule.enabled}
                          handleOnChange={(e) =>
                            setFieldValue(
                              `planWiseRules[${index}].enabled`,
                              e.value,
                            )
                          }
                        />
                      </div>
                    </Col>

                    {rule.enabled && (
                      <>
                        {/* Max Pause Count */}
                        <Col lg={3}>
                          <div className="form_field">
                            <SingleSelect
                              label={t("Max Pause limit")}
                              options={
                                MAXIMUM_VALUES_MAX_TRANSFER_AND_MAX_PAUSES
                              }
                              value={rule.maxPauseCount}
                              handleSingleSelectChange={(opt) =>
                                setFieldValue(
                                  `planWiseRules[${index}].maxPauseCount`,
                                  opt.value,
                                )
                              }
                            />
                          </div>
                        </Col>
                        {/* Unlimited Toggle */}
                        <Col lg={3}>
                          <div className="form_field">
                            <Switch
                              label={t("Allow Unlimited Pause Duration")}
                              checked={rule.isUnlimited}
                              infoText={t(
                                "Customers can pause for any number of days",
                              )}
                              handleOnChange={(e) =>
                                setFieldValue(
                                  `planWiseRules[${index}].isUnlimited`,
                                  e.value,
                                )
                              }
                            />
                          </div>
                        </Col>
                        {/* Min Pause Days */}
                        <Col lg={3}>
                          <div className="form_field">
                            <CustomField
                              label={t("Minimum Pause Duration (Days)")}
                              name={`planWiseRules[${index}].minPauseDays`}
                              infoText={t(
                                "Minimum number of days a customer can pause",
                              )}
                              type="number"
                              value={rule.minPauseDays}
                              placeholder={t("Enter Minimum Pause Duration")}
                              disabled={rule.isUnlimited}
                            />
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div className="form_field">
                            <CustomField
                              label={t("Maximum Pause Duration (Days)")}
                              name={`planWiseRules[${index}].maxPauseDays`}
                              infoText={t(
                                "Maximum number of days allowed per pause",
                              )}
                              type="number"
                              value={rule.maxPauseDays}
                              placeholder={t("Enter Maximum Pause Duration")}
                              disabled={rule.isUnlimited}
                            />
                          </div>
                        </Col>{" "}
                      </>
                    )}
                  </React.Fragment>
                ))}

                {/* Auto Resume */}
                <Col lg={12}>
                  <div className="form_field">
                    <Switch
                      label={t("Auto Resume")}
                      checked={values.autoResume}
                      handleOnChange={(e) =>
                        setFieldValue("autoResume", e.value)
                      }
                    />
                    <p className="text-muted small mt-1">
                      {t("auto resume after pause duration")}
                    </p>
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={pauseRule.showConfirm}
          onCancel={pauseRule.handleCancel}
          onConfirm={pauseRule.handleConfirm}
          title="Save Pause Rule"
          message="Are you sure you want to update the membership pause rules?"
        />
      </div>
      {/* ──────────────────── MEMBERSHIP TRANSFER RULE ──────────────────── */}
      <div className="common_card mt-4">
        <div className="position-relative d-flex align-items-center">
          <h4 className="section_title">{t("Membership Transfer Rule")}</h4>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {t(
                  "Transfer means reassigning membership from one vehicle to another under the same account",
                )}
              </Tooltip>
            }
          >
            <img
              src={info}
              alt="info"
              width={20}
              height={20}
              style={{ cursor: "pointer" }}
              className="ms-1"
            />
          </OverlayTrigger>
        </div>

        <Formik
          initialValues={transferRuleInitial}
          enableReinitialize
          validationSchema={transferRuleValidationSchema}
          onSubmit={transferRule.handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <Col lg={4}>
                  <div className="form_field">
                    <SingleSelect
                      label={t("Maximum Transfers Allowed")}
                      name={`maxTransfers`}
                      options={MAXIMUM_VALUES_MAX_TRANSFER_AND_MAX_PAUSES}
                      value={values?.maxTransfers}
                      placeholder={t("Select max transfers")}
                      handleSingleSelectChange={(opt) =>
                        setFieldValue(`maxTransfers`, opt.value)
                      }
                      infoText={t(
                        "Maximum number of times a membership can be transferred to another vehicle",
                      )}
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Transfer Cooldown (Days)")}
                      name="cooldownDays"
                      type="number"
                      value={values.cooldownDays}
                      placeholder={t("Enter cooldown days")}
                      infoText={t(
                        "Number of days required between two transfers",
                      )}
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Minimum Days Before First Transfer")}
                      name="minDaysBeforeTransfer"
                      type="number"
                      value={values.cooldownDays}
                      placeholder={t("Enter minimum days")}
                      infoText={t(
                        "Minimum number of days after purchase before transfer is allowed",
                      )}
                    />
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={transferRule.showConfirm}
          onCancel={transferRule.handleCancel}
          onConfirm={transferRule.handleConfirm}
          title="Save Transfer Rule"
          message="Are you sure you want to update the membership transfer rules?"
        />
      </div>
      {/* ──────────────────── VAT INFO ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Tax Settings (VAT)")}</h4>

        <Formik
          initialValues={vatInfoInitial}
          enableReinitialize
          validationSchema={vatInfoValidationSchema}
          onSubmit={vatInfo.handleFormSubmit}
        >
          {({ values }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("VAT Name")}
                      name="title"
                      type="text"
                      placeholder="Title"
                      enableTranslate
                      translateTo="ar"
                      targetField="titleAr"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("VAT Name (Arabic)")}
                      name="titleAr"
                      type="text"
                      placeholder="Title ar"
                      className=""
                      enableTranslate
                      translateTo="en"
                      targetField="title"
                      isArabic
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("VAT Rate (%)")}
                      name="value"
                      type="number"
                      placeholder="value"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("VAT Registration Number")}
                      name="registeredNo"
                      type="number"
                      placeholder="Registered Number"
                    />
                  </div>
                </Col>
                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={vatInfo.showConfirm}
          onCancel={vatInfo.handleCancel}
          onConfirm={vatInfo.handleConfirm}
          title="Save VAT Settings"
          message="Are you sure you want to update the VAT information?"
        />
      </div>
      {/* ──────────────────── REFERRAL REWARD CONFIGURATION ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Referral Reward Settings")}</h4>

        <Formik
          initialValues={referralRewardInitial}
          enableReinitialize
          validationSchema={referralRewardValidationSchema}
          onSubmit={referral.handleFormSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <h6 className="mt-5">{t("Referral (Sender) Rewards")}</h6>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Sender Points")}
                      name="senderPoints"
                      type="number"
                      placeholder="Enter Sender Points"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      label={t("When to Reward Referrer (Sender)")}
                      name={`senderTriggerOn`}
                      options={REWARD_TRIGGERING_TYPES_SENDER}
                      value={values?.senderTriggerOn}
                      handleSingleSelectChange={(opt) =>
                        setFieldValue(`senderTriggerOn`, opt.value)
                      }
                      error={
                        errors.senderTriggerOn && touched.senderTriggerOn
                          ? errors.senderTriggerOn
                          : null
                      }
                    />
                  </div>
                </Col>
                <h6 className="">{t("New Customer Rewards")}</h6>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Referred (Receiver) Customer Reward Points")}
                      name="receiverPoints"
                      type="number"
                      placeholder="Enter Receiver Points"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      label={t("When to Reward New Customer")}
                      name={`receiverTriggerOn`}
                      options={REWARD_TRIGGERING_TYPES_RECEIVER}
                      value={values?.receiverTriggerOn}
                      handleSingleSelectChange={(opt) =>
                        setFieldValue(`receiverTriggerOn`, opt.value)
                      }
                      error={
                        errors.receiverTriggerOn && touched.receiverTriggerOn
                          ? errors.receiverTriggerOn
                          : null
                      }
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Points Validity (Days)")}
                      name="expiryDays"
                      type="number"
                      placeholder="Enter Expiry Days"
                    />
                  </div>
                </Col>
                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={referral.showConfirm}
          onCancel={referral.handleCancel}
          onConfirm={referral.handleConfirm}
          title="Save Referral Reward Settings"
          message="Are you sure you want to update the referral reward settings?"
        />
      </div>
      {/* ──────────────────── REDEMPTION POINTS SETTINGS ──────────────────── */}
      <div className="common_card mt-4">
        <h4 className="section_title">{t("Redemption Points Settings")}</h4>

        <Formik
          initialValues={loyaltyPointsRedemptionInitial}
          enableReinitialize
          validationSchema={loyaltyPointsRedemptionValidationSchema}
          onSubmit={loyalty.handleFormSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Row className="g-4">
                {/* Max Transfers */}
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Points Required per SAR")}
                      infoText={
                        "Number of loyalty points needed to redeem 1 SAR."
                      }
                      name="pointsPerSAR"
                      type="number"
                      placeholder="Enter Points"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Minimum Points Required to Redeem")}
                      infoText={
                        "Minimum points a customer must have to redeem."
                      }
                      name="minimumRedemptionPoints"
                      type="number"
                      placeholder="Enter Points"
                    />
                  </div>
                </Col>
                {/* Buttons */}
                <Col lg={12} className="mt-2 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="main_btn">
                    {t("Save")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        <ConfirmDialog
          show={loyalty.showConfirm}
          onCancel={loyalty.handleCancel}
          onConfirm={loyalty.handleConfirm}
          title="Save Redemption Settings"
          message="Are you sure you want to update the loyalty points redemption settings?"
        />
      </div>
    </div>
  );
};

export default AppSettings;
